/**
 * Calculateur de conformité 2026 — Lead Magnet
 * Public page : /calculateur-conformite-2026
 * Goal: capture emails + qualify leads + push them to /register.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft, Check, AlertTriangle, Sparkles, Zap, ShieldCheck } from 'lucide-react';
import { api } from '../services/api';

const QUESTIONS = [
  {
    id: 'q_status',
    label: 'Quel est le statut juridique de votre entreprise ?',
    type: 'choice',
    options: [
      { value: 'micro', label: 'Auto-entrepreneur / Micro-entreprise' },
      { value: 'eurl', label: 'EURL / SASU' },
      { value: 'sarl', label: 'SARL / SAS' },
      { value: 'freelance', label: 'Freelance (profession libérale)' },
      { value: 'autre', label: 'Autre' },
    ],
  },
  {
    id: 'q_has_siret',
    label: 'Avez-vous un numéro SIRET valide ?',
    type: 'boolean',
  },
  {
    id: 'q_tva_assujetti',
    label: 'Êtes-vous assujetti à la TVA ?',
    type: 'boolean',
  },
  {
    id: 'q_invoices_per_month',
    label: 'Combien de factures émettez-vous par mois en moyenne ?',
    type: 'choice',
    options: [
      { value: '1-5', label: '1 à 5 factures' },
      { value: '6-20', label: '6 à 20 factures' },
      { value: '21-50', label: '21 à 50 factures' },
      { value: '50+', label: 'Plus de 50 factures' },
    ],
  },
  {
    id: 'q_current_tool',
    label: 'Quel outil utilisez-vous actuellement pour vos factures ?',
    type: 'choice',
    options: [
      { value: 'word-excel', label: 'Word / Excel' },
      { value: 'autre-logiciel', label: 'Autre logiciel de facturation' },
      { value: 'smartfacture', label: 'SmartFacture AI' },
      { value: 'rien', label: 'Rien pour l\'instant' },
    ],
  },
  {
    id: 'q_knows_factur_x',
    label: 'Savez-vous ce qu\'est le format Factur-X ?',
    type: 'boolean',
  },
  {
    id: 'q_knows_ppf_pdp',
    label: 'Connaissez-vous le PPF (Portail Public de Facturation) et les PDP ?',
    type: 'boolean',
  },
  {
    id: 'q_has_mentions',
    label: 'Vos factures incluent-elles toutes les mentions légales obligatoires (SIREN, TVA, conditions de paiement) ?',
    type: 'boolean',
  },
  {
    id: 'q_archive_years',
    label: 'Combien de temps archivez-vous vos factures ?',
    type: 'choice',
    options: [
      { value: '<1', label: 'Moins d\'1 an' },
      { value: '1-5', label: '1 à 5 ans' },
      { value: '6-10', label: '6 à 10 ans' },
      { value: '10+', label: '10 ans et plus (obligation légale)' },
    ],
  },
  {
    id: 'q_ready_for_reform',
    label: 'Vous sentez-vous prêt(e) pour la réforme du 1er septembre 2026 ?',
    type: 'boolean',
  },
];

const LEVEL_STYLES = {
  ready: { color: 'emerald', icon: Check, label: 'Prêt pour 2026' },
  partially_ready: { color: 'amber', icon: Sparkles, label: 'Presque prêt' },
  at_risk: { color: 'orange', icon: AlertTriangle, label: 'En risque' },
  critical: { color: 'red', icon: AlertTriangle, label: 'Alerte critique' },
};

export default function CalculateurConformitePage() {
  const [step, setStep] = useState(-1); // -1 = intro, 0..N-1 = questions, N = email, N+1 = result
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const total = QUESTIONS.length;
  const question = step >= 0 && step < total ? QUESTIONS[step] : null;
  const progress = step < 0 ? 0 : Math.min(100, Math.round(((step + 1) / (total + 1)) * 100));

  const answer = (value) => {
    setAnswers((a) => ({ ...a, [question.id]: value }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        email: email || undefined,
        answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })),
      };
      const { data } = await api.post('/compliance-calculator/score', payload);
      setResult(data);
      setStep(total + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors du calcul. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(-1);
    setAnswers({});
    setEmail('');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#FAFFF7]" data-testid="compliance-calculator-page">
      <Helmet>
        <title>Calculateur de conformité facturation 2026 — Êtes-vous prêt ? | SmartFacture AI</title>
        <meta name="description" content="Testez gratuitement votre conformité à la réforme de la facturation électronique 2026 en 2 minutes. Score personnalisé + plan d'action par email." />
        <link rel="canonical" href="https://smartfacture-ai.com/calculateur-conformite-2026" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-5 py-10">
        {/* Header / Progress */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold mb-4">
            <ShieldCheck className="h-3.5 w-3.5" />
            Calculateur gratuit · 2 minutes
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Êtes-vous prêt pour la<br />
            <span className="text-emerald-600">facturation électronique 2026</span> ?
          </h1>
          <p className="text-slate-600 max-w-md mx-auto">
            Testez votre conformité en 10 questions rapides. Score personnalisé + plan d'action instantané.
          </p>
        </div>

        {step >= 0 && step <= total && (
          <div className="mb-6" aria-hidden="true">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-xs text-slate-500 mt-1.5 text-right">
              {Math.min(step + 1, total + 1)} / {total + 1}
            </div>
          </div>
        )}

        {/* Intro */}
        {step === -1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-8 text-center">
            <div className="flex flex-col gap-4 mb-6 text-left">
              {[
                { icon: Sparkles, text: 'Évaluation instantanée de votre niveau de conformité' },
                { icon: Zap, text: 'Plan d\'action personnalisé en fonction de votre situation' },
                { icon: ShieldCheck, text: '100% gratuit, sans carte bancaire' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-emerald-700" />
                  </div>
                  <span className="text-sm text-slate-700">{text}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setStep(0)}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
              data-testid="compliance-start"
            >
              Commencer le test <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Question step */}
        {question && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">
              Question {step + 1}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 leading-snug">
              {question.label}
            </h2>

            {question.type === 'boolean' && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => answer(true)}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 font-semibold text-slate-700 transition"
                  data-testid="compliance-answer-yes"
                >
                  Oui
                </button>
                <button
                  onClick={() => answer(false)}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 font-semibold text-slate-700 transition"
                  data-testid="compliance-answer-no"
                >
                  Non
                </button>
              </div>
            )}

            {question.type === 'choice' && (
              <div className="space-y-2">
                {question.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => answer(opt.value)}
                    className="w-full text-left p-3.5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 transition flex items-center justify-between group"
                    data-testid="compliance-answer-choice"
                  >
                    <span>{opt.label}</span>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 transition" />
                  </button>
                ))}
              </div>
            )}

            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="mt-5 text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 transition"
                data-testid="compliance-back"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Précédent
              </button>
            )}
          </div>
        )}

        {/* Email step */}
        {step === total && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
              Votre score personnalisé vous attend ✨
            </h2>
            <p className="text-slate-600 mb-5 text-sm">
              Entrez votre email pour recevoir votre score de conformité et un plan d'action détaillé. (Optionnel, vous pouvez aussi voir le résultat directement.)
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.fr"
              className="w-full px-4 py-3.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 mb-4 text-sm"
              data-testid="compliance-email-input"
            />
            <button
              onClick={submit}
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
              data-testid="compliance-submit"
            >
              {loading ? 'Calcul en cours...' : 'Voir mon score'}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
            {error && <div className="text-sm text-red-600 mt-3">{error}</div>}
            <p className="text-xs text-slate-400 text-center mt-3">
              Nous ne spammons pas. Désabonnement en 1 clic.
            </p>
          </div>
        )}

        {/* Result step */}
        {step === total + 1 && result && (
          <ResultCard result={result} onRestart={restart} />
        )}
      </div>
    </div>
  );
}

function ResultCard({ result, onRestart }) {
  const levelCfg = LEVEL_STYLES[result.level] || LEVEL_STYLES.at_risk;
  const Icon = levelCfg.icon;
  const color = levelCfg.color;
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden" data-testid="compliance-result">
      <div
        className={`px-8 pt-8 pb-12 text-center relative`}
        style={{
          background:
            color === 'emerald'
              ? 'linear-gradient(135deg, #DCF5C8 0%, #C9EFB4 50%, #B5E89F 100%)'
              : color === 'amber'
              ? 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 50%, #FACC15 100%)'
              : color === 'orange'
              ? 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 50%, #FB923C 100%)'
              : 'linear-gradient(135deg, #FEE2E2 0%, #FCA5A5 50%, #EF4444 100%)',
        }}
      >
        <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur px-4 py-1.5 rounded-full text-xs font-semibold text-slate-800 mb-4">
          <Icon className="h-3.5 w-3.5" /> {levelCfg.label}
        </div>
        <div className="text-6xl font-bold text-slate-900 mb-1">{result.score}<span className="text-2xl text-slate-600">/100</span></div>
        <div className="text-sm text-slate-700 font-medium">Votre score de conformité</div>
      </div>

      <div className="px-8 py-8">
        <p className="text-slate-700 leading-relaxed mb-6">{result.summary}</p>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Plan d'action</div>
        <ul className="space-y-2.5 mb-8">
          {(result.actions || []).map((a, i) => (
            <li key={i} className="flex gap-3 items-start">
              <div className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="h-3 w-3 text-emerald-700" strokeWidth={3} />
              </div>
              <span className="text-sm text-slate-700">{a}</span>
            </li>
          ))}
        </ul>

        <Link
          to="/register"
          className="block w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-center py-4 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
          data-testid="compliance-cta-register"
        >
          Me mettre en conformité avec SmartFacture AI — essai gratuit
        </Link>
        <button
          onClick={onRestart}
          className="w-full text-center text-sm text-slate-500 hover:text-slate-700 py-3 transition mt-2"
          data-testid="compliance-restart"
        >
          Refaire le test
        </button>
      </div>
    </div>
  );
}
